<template>
  <v-container class="p-5 mb-5">
    <v-row class="mb-5">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <h1 class="text-3xl font-bold">Website Analytics</h1>
        <div>
          <v-btn color="grey darken-2" class="mr-3" href="/admin/analytics/search">
            Advanced Search
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row class="mb-5">
      <v-col cols="12" md="4" >
        <v-card class="pa-5 text-center" elevation="4" rounded="lg">
            <v-icon color="blue" size="48">mdi-web</v-icon>
           <h2 class="text-lg font-semibold  mb-2">Total Page Views</h2>
           <p class="">{{ totalPageViews }}</p>
         </v-card>
       </v-col>
       <v-col cols="12" md="4">
         <v-card class="pa-5 text-center" elevation="4" rounded="lg">
        <v-icon color="green" size="48">mdi-calendar-today</v-icon>
           <h2 class="text-lg font-semibold mb-2">Today's Page Views</h2>
           <p class="text-4xl font-extrabold ">{{ todayPageViews }}</p>
         </v-card>
       </v-col>
       <v-col cols="12" md="4">
         <v-card class="pa-5 text-center" elevation="4" rounded="lg">
                <v-icon color="purple" size="48">mdi-ip</v-icon>
           <h2 class="text-lg font-semibold text-gray-700 mb-2">Unique IPs Today</h2>
           <p class="text-4xl font-extrabold text-purple-600">{{ uniqueIPsToday }}</p>
         </v-card>
      </v-col>
    </v-row>

    <v-card class="mb-5" v-if="0">
      <v-card-title>Daily Page Views (Last 30 Days)</v-card-title>
      <v-card-text>
        <div style="height: 320px;">
          <LineChart :chart-data="dailyPageViewsChartData" :options="chartOptions" />
        </div>
      </v-card-text>
    </v-card>

    <v-row class="mt-5">
      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>Top Visited Pages</v-card-title>
          <v-data-table
            :headers="topPagesHeaders"
            :items="topPages"
            class="elevation-1"
          >
            <template v-slot:item.path="{ item }">
              <a :href="item.path" target="_blank" rel="noopener noreferrer">{{ truncateUrl(item.path) }}</a>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>Recent Visitors</v-card-title>
          <v-data-table
            :headers="recentVisitorsHeaders"
            :items="recentPageViews"
            class="elevation-1"
          >
            <template v-slot:item.visited_at="{ item }">
              {{ formatDateTime(item.visited_at) }}
            </template>
            <template v-slot:item.path="{ item }">
              {{ truncateUrl(item.path) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// No direct imports for Chart.js components here, they are registered globally in app.js
import axios from 'axios'; // Ensure axios is available globally or imported
  import Layout from '@/Shared/VuetifyAdminLayout'
export default {
  // Inertia props are accessed directly via `this` in Vue 2
  props: {
    totalPageViews: Number,
    todayPageViews: Number,
    uniqueIPsToday: Number,
  },
  layout: Layout,
  data() {
    return {
      dailyPageViewsChartData: {
        labels: [],
        datasets: [{
          label: 'Daily Page Views',
          backgroundColor: 'rgba(59, 130, 246, 0.5)', // Tailwind blue-500 with transparency
          borderColor: '#3b82f6', // Tailwind blue-500
          tension: 0.3, // Smoother line
          data: [],
          fill: true,
        }]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                if (value % 1 === 0) {
                  return value;
                }
              },
            },
          },
        },
      },
      topPages: [],
      recentPageViews: [],
      topPagesHeaders: [
        { text: 'URL Path', value: 'path' },
        { text: 'Views', value: 'count' },
      ],
      recentVisitorsHeaders: [
        { text: 'Time', value: 'visited_at' },
        { text: 'IP Address', value: 'ip_address' },
        { text: 'Path', value: 'path' },
      ],
    };
  },
  methods: {
    // --- Data Fetching Functions ---
    async fetchDailyPageViews() {
      try {
        // Use this.route() as it's made global in app.js for Vue 2
        const response = await axios.get('/admin/analytics/daily-page-views?days=30');
        const data = response.data;

        const dateMap = new Map();
        const today = new Date();
        for (let i = 29; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          dateMap.set(d.toISOString().split('T')[0], 0);
        }

        data.forEach(item => {
          dateMap.set(item.date, item.count);
        });

        const labels = Array.from(dateMap.keys()).map(dateString => {
          const parts = dateString.split('-');
          return `${parseInt(parts[1])}/${parseInt(parts[2])}`; // MM/DD format
        });
        const counts = Array.from(dateMap.values());

        this.dailyPageViewsChartData.labels = labels;
        this.dailyPageViewsChartData.datasets[0].data = counts;

      } catch (error) {
        console.error('Error fetching daily page views:', error);
      }
    },

    async fetchTopPages() {
      try {
        const response = await axios.get('/admin/analytics/top-pages?limit=10');
        this.topPages = response.data;
      } catch (error) {
        console.error('Error fetching top pages:', error);
      }
    },

    async fetchRecentPageViews() {
      try {
        const response = await axios.get('/admin/analytics/recent-page-views?limit=20');
        this.recentPageViews = response.data;
      } catch (error) {
        console.error('Error fetching recent page views:', error);
      }
    },

    // --- Utility Functions ---
    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString();
    },

    truncateUrl(url, maxLength = 50) {
      if (!url) return '';
      return url.length > maxLength ? url.substring(0, maxLength - 3) + '...' : url;
    },
  },
  // Lifecycle Hook: mounted() is equivalent to onMounted() in Vue 3 Composition API
  mounted() {
    this.fetchDailyPageViews();
    this.fetchTopPages();
    this.fetchRecentPageViews();
  },
};
</script>

<style scoped>
/* Scoped styles for this component, if any, will go here */
</style>